import { AppointmentResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { dateLocalString } from "src/lib/date";
import translate from "src/lib/translate";
import {
  AppPopup,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import LabStatusOverviewCubit from "../LabStatusOverview/LabStatusOverviewCubit";

export type BookingConfirmationOptions = Parameters<
  typeof BlockingLoadingOverlayController.loadingSuccess
>[0];

export default class AppointmentSchedulerCubit extends Cubit<
  Record<string, never>
> {
  constructor() {
    super({});
  }

  /**
   * Build the confirmation overlay options for a freshly booked appointment.
   * When the member still has incomplete required labs we nudge them to get
   * their records ready (the Care Team relies on up-to-date results); otherwise
   * we show a plain confirmation that dismisses itself. Labs that are already
   * in progress (requisition issued or an upload under review) count as handled,
   * so we skip the nudge even though the fulfillment status is still incomplete.
   */
  resolveBookingConfirmationOptions = async (
    appointment: AppointmentResponse
  ): Promise<BookingConfirmationOptions> => {
    const title = translate("appointment.confirmation.title", {
      dateTime: dateLocalString(appointment.start)
    });

    const labStatus = Blac.getBloc(LabStatusOverviewCubit);
    await labStatus.fetchLabStatus(true);

    const { hasInitialLabsInProgress } = Blac.getBloc(TaskManagementBloc);

    if (labStatus.hasIncompleteRequiredLabs && !hasInitialLabsInProgress) {
      return {
        title,
        message: "appointment.confirmation.labs.incomplete.description",
        showIcon: false,
        primaryAction: {
          text: "appointment.confirmation.labs.cta",
          onClick: () => AppQueryPopupsController.openPopup(AppPopup.initialLab)
        },
        secondaryAction: {
          text: "notNow",
          onClick: () => undefined
        }
      };
    }

    return {
      title,
      autoDismiss: true,
      primaryAction: {
        text: "close",
        onClick: () => undefined
      }
    };
  };

  showBookingConfirmation = (options: BookingConfirmationOptions): void => {
    BlockingLoadingOverlayController.loadingSuccess(options);
  };
}
